/**
 * Regras do videowall multi-monitor (configuração, janelas e monitores) — Nexora Grid (frontend).
 *
 * Criado em: 02/11/2026 · Última modificação: 02/11/2026 · Versão: 1.0.0
 *
 * Funções puras (sem DOM), testadas em `testes/videowall.test.mjs`. Cada tela pode ficar na grade desta janela ou
 * ir para uma janela própria, levada a outro monitor; a posição dessa janela fica guardada por tela.
 */

export const MIN_TELAS = 1;
export const MAX_TELAS = 12;

export const OPCOES_TELA = [
  { valor: "/cco", texto: "CCO — visão geral" },
  { valor: "/cco/alarmes", texto: "CCO — alarmes" },
  { valor: "/cco/mapa", texto: "CCO — Mapa de Comando" },
  { valor: "/cco/qualidade", texto: "CCO — qualidade dos sensores" },
  { valor: "/cco/gestor", texto: "CCO — gestor" },
  { valor: "/rede", texto: "Rede" },
  { valor: "/scada3d", texto: "SCADA 3D" },
];

/** Posição e tamanho de uma janela na área de trabalho estendida (todos os monitores), em px. */
export type Posicao = { left: number; top: number; width: number; height: number };
/** Monitor como o navegador o descreve (`ScreenDetailed`): só a área útil e o nome. */
export type Monitor = { availLeft: number; availTop: number; availWidth: number; availHeight: number; label?: string; isPrimary?: boolean };
/** Configuração deste computador: quantas telas, o que cada uma mostra e onde fica a janela de cada uma. */
export type Config = { telas: number; conteudos: string[]; janelas: (Posicao | null)[] };

const PADRAO: Config = { telas: 4, conteudos: ["/cco", "/cco/alarmes", "/rede", "/scada3d"], janelas: [] };

export function limitarTelas(n: number): number {
  return Math.min(MAX_TELAS, Math.max(MIN_TELAS, Math.round(Number(n)) || MIN_TELAS));
}

function posicaoValida(p: unknown): p is Posicao {
  const q = p as Posicao;
  return !!q && [q.left, q.top, q.width, q.height].every(Number.isFinite) && q.width >= 200 && q.height >= 150;
}

/** Lê a configuração guardada (texto JSON); qualquer coisa inválida volta ao padrão, campo a campo. */
export function lerConfig(texto: string | null): Config {
  let c: Partial<Config> = {};
  try { c = JSON.parse(texto ?? "") ?? {}; } catch { return { ...PADRAO }; }
  if (!(Number(c.telas) >= MIN_TELAS && Number(c.telas) <= MAX_TELAS)) return { ...PADRAO };
  const validos = new Set(OPCOES_TELA.map((o) => o.valor));
  const conteudos = Array.isArray(c.conteudos) ? c.conteudos.map((v) => (validos.has(v) ? v : "/cco")) : [];
  const janelas = Array.isArray(c.janelas) ? c.janelas.map((p) => (posicaoValida(p) ? p : null)) : [];
  return { telas: limitarTelas(Number(c.telas)), conteudos, janelas };
}

export function conteudoDaTela(config: Config, i: number): string {
  return config.conteudos[i] ?? "/cco";
}

/** Endereço da tela: a página da própria aplicação em modo embutido; `janela=1` quando vai para uma janela própria. */
export function urlDaTela(conteudo: string, i: number, janela: boolean, modo?: string): string {
  return `${conteudo}${conteudo.includes("?") ? "&" : "?"}embutido=1&tela=${i + 1}${janela ? "&janela=1" : ""}${modo ? `&modo=${modo}` : ""}`;
}

/** Nome fixo da janela de cada tela: abrir de novo reaproveita a mesma janela em vez de criar outra. */
export function nomeDaJanela(i: number): string {
  return `nexora-videowall-tela-${i + 1}`;
}

/** Colunas da grade desta janela para as telas que ficaram nela (quadrado mais próximo). */
export function colunasDaGrade(naGrade: number): number {
  return Math.max(1, Math.ceil(Math.sqrt(Math.max(1, naGrade))));
}

/** Recursos do `window.open`: janela solta, sem barra, na posição guardada (ou num tamanho padrão). */
export function recursosDaJanela(p: Posicao | null): string {
  const base = "popup=yes,menubar=no,toolbar=no,location=no,status=no";
  return p ? `${base},left=${Math.round(p.left)},top=${Math.round(p.top)},width=${Math.round(p.width)},height=${Math.round(p.height)}` : `${base},width=1280,height=720`;
}

/** Área útil inteira de um monitor: a janela da tela ocupa o monitor todo. */
export function posicaoNoMonitor(m: Monitor): Posicao {
  return { left: m.availLeft, top: m.availTop, width: m.availWidth, height: m.availHeight };
}

/** Em qual monitor está o centro da janela (-1 se em nenhum dos detectados). */
export function monitorDaPosicao(p: Posicao | null, monitores: Monitor[]): number {
  if (!p) return -1;
  const x = p.left + p.width / 2, y = p.top + p.height / 2;
  return monitores.findIndex((m) => x >= m.availLeft && x < m.availLeft + m.availWidth && y >= m.availTop && y < m.availTop + m.availHeight);
}

export function nomeDoMonitor(m: Monitor, i: number): string {
  return `${m.label?.trim() || `Monitor ${i + 1}`} · ${m.availWidth}×${m.availHeight}${m.isPrimary ? " (principal)" : ""}`;
}

/** Diferença que vale guardar: a janela mexeu ou mudou de tamanho (ignora ruído de 1–2 px). */
export function mudouPosicao(a: Posicao | null, b: Posicao): boolean {
  return !a || Math.abs(a.left - b.left) > 2 || Math.abs(a.top - b.top) > 2 || Math.abs(a.width - b.width) > 2 || Math.abs(a.height - b.height) > 2;
}

/** Grava a posição da janela de uma tela, mantendo as outras. */
export function comJanela(config: Config, i: number, p: Posicao | null): Config {
  const janelas = Array.from({ length: Math.max(config.janelas.length, i + 1) }, (_, k) => config.janelas[k] ?? null);
  janelas[i] = p;
  return { ...config, janelas };
}
